// src/prototype/components/sep/SEPSectionPortfolioImpact.tsx

import React from 'react';
import { TrendingUp, DollarSign } from 'lucide-react';

interface Finalist {
  id: string;
  title: string;
  category: string;
  implementationEffort: 'Low' | 'Medium' | 'High';
  timeToValue: string;
  roiEstimate: { low: number; high: number; unit: string };
  scenarios: {
    conservative: { roiLow: number; roiHigh: number; paybackMonths: number };
    base: { roiLow: number; roiHigh: number; paybackMonths: number };
    aggressive: { roiLow: number; roiHigh: number; paybackMonths: number };
  };
}

export interface SEPSectionPortfolioImpactProps {
  finalists: Finalist[];
  portfolioRoiLow: number;
  portfolioRoiHigh: number;
}

const COLORS = ['#18365E', '#FF6E4C', '#2563EB'];

const fmt = (n: number) => `$${n.toFixed(1)}M`;

export const SEPSectionPortfolioImpact: React.FC<SEPSectionPortfolioImpactProps> = ({
  finalists, portfolioRoiLow, portfolioRoiHigh,
}) => {
  const maxHigh = Math.max(...finalists.map(f => f.roiEstimate.high));

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl p-6 mb-6">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-[3px] self-stretch rounded-full bg-[#FF6E4C] shrink-0 min-h-[40px]" />
        <div>
          <p className="text-[10px] font-bold text-[#FF6E4C] uppercase tracking-widest mb-0.5">Section 3</p>
          <h2 className="text-lg font-bold text-[#18365E] leading-tight">Portfolio Financial Impact</h2>
          <p className="text-[11px] text-[#717182] mt-0.5">Combined ROI potential by initiative and scenario-based projections</p>
        </div>
      </div>

      <p className="text-sm text-[#18365E]/80 leading-relaxed mb-6">
        The selected portfolio represents a combined annual value opportunity of{' '}
        <strong>{fmt(portfolioRoiLow)}–{fmt(portfolioRoiHigh)}</strong> across all initiatives.
        Each initiative carries independently validated ROI assumptions derived from industry benchmarks and{' '}
        {finalists.length > 1 ? `your ${finalists.length} selected programs` : 'your selected program'}.
      </p>

      {/* Bar chart */}
      <div className="mb-6">
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-3">Initiative ROI Comparison</p>
        <div className="bg-[#F5F7FA] rounded-xl p-4 border border-[#e5e7eb] space-y-4">
          {finalists.map((f, i) => (
            <div key={f.id}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                  <p className="text-xs font-semibold text-[#18365E] leading-tight">{f.title}</p>
                </div>
                <p className="text-xs font-bold" style={{ color: COLORS[i % COLORS.length] }}>
                  ${f.roiEstimate.low}–${f.roiEstimate.high}{f.roiEstimate.unit}/yr
                </p>
              </div>
              {/* Range bar */}
              <div className="relative h-4 bg-white rounded-full border border-[#e5e7eb] overflow-hidden">
                <div
                  className="absolute top-0 bottom-0 rounded-full"
                  style={{
                    left: `${(f.roiEstimate.low / maxHigh) * 85}%`,
                    width: `${((f.roiEstimate.high - f.roiEstimate.low) / maxHigh) * 85}%`,
                    background: COLORS[i % COLORS.length],
                    opacity: 0.25,
                  }}
                />
                <div
                  className="absolute top-0 bottom-0 rounded-full"
                  style={{
                    left: `${(f.roiEstimate.low / maxHigh) * 85}%`,
                    width: `${(f.roiEstimate.low / maxHigh) * 5}%`,
                    background: COLORS[i % COLORS.length],
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scenario table */}
      <div>
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-3">Scenario Analysis — Combined Portfolio</p>
        <div className="border border-[#e5e7eb] rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[#F5F7FA] border-b border-[#e5e7eb]">
                <th className="text-left px-4 py-2.5 font-semibold text-[#717182] w-[160px]">Initiative</th>
                {['Conservative', 'Base Case', 'Aggressive'].map(s => (
                  <th key={s} className="text-center px-3 py-2.5 font-semibold text-[#717182]">{s}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {finalists.map((f, i) => (
                <tr key={f.id} className={i < finalists.length - 1 ? 'border-b border-[#e5e7eb]' : ''}>
                  <td className="px-4 py-3 font-medium text-[#18365E]">{f.title.split(' ').slice(0, 3).join(' ')}&hellip;</td>
                  {(['conservative', 'base', 'aggressive'] as const).map(s => (
                    <td key={s} className="px-3 py-3 text-center">
                      <p className="font-bold text-[#18365E]">{fmt(f.scenarios[s].roiHigh)}</p>
                      <p className="text-[10px] text-[#717182]">{f.scenarios[s].paybackMonths}mo payback</p>
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="bg-[#18365E]/[0.04] border-t border-[#18365E]/10">
                <td className="px-4 py-3 font-bold text-[#18365E] text-[11px] uppercase tracking-wide">Total Portfolio</td>
                {(['conservative', 'base', 'aggressive'] as const).map(s => {
                  const total = finalists.reduce((acc, f) => acc + f.scenarios[s].roiHigh, 0);
                  return (
                    <td key={s} className="px-3 py-3 text-center">
                      <p className="font-bold text-[#FF6E4C] text-sm">{fmt(total)}</p>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SEPSectionPortfolioImpact;